import { AuthenticationService } from '@core/services/authentication/authentication-service'
import {
  addPushNotificationsToken as addPushNotificationsTokenService,
  removePushNotificationsToken as removePushNotificationsTokenService,
  triggerPushNotification as triggerPushNotificationService,
  WEB_DEVICE_TYPE,
} from '@core/services/push-notifications/push-notifications-service'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'
import type { UserNotification } from '@domain/models/user-notifications'

interface NotificationDestination {
  pathname: string
  search?: string
}

interface EnqueuePushForInAppNotificationParams {
  forUserId: string
  notification: Partial<UserNotification>
  destination: NotificationDestination
}

const USERS_COLLECTION_PATH = 'users'

function getAuthUserIdOrThrow(): string {
  const userId = AuthenticationService.getInstance().userId
  if (!userId) {
    throw new CoreError({
      cause: CoreErrorCause.Forbidden,
      context: 'No authenticated user available for push notifications feature.',
    })
  }
  return userId
}

function normalizeRoute(destination: NotificationDestination): string {
  const pathname = destination.pathname.trim() || '/'
  return `${pathname}${destination.search ?? ''}`
}

function toUserRefPath(userId: string): string {
  return `${USERS_COLLECTION_PATH}/${userId.trim()}`
}

function toNotificationTitle(notification: Partial<UserNotification>): string {
  return notification.title?.trim() || 'Activity update'
}

function toNotificationText(notification: Partial<UserNotification>): string {
  return notification.description?.trim() || 'Open to view the latest activity on your content.'
}

export class PushNotificationsFeature {
  private static readonly tokenSyncInFlightByUser = new Map<string, Promise<string | null>>()

  public static async syncTokenForAuthenticatedUser(userId?: string): Promise<string | null> {
    const resolvedUserId = (userId ?? getAuthUserIdOrThrow()).trim()
    if (!resolvedUserId) {
      throw new CoreError({
        cause: CoreErrorCause.Forbidden,
        context: 'No authenticated user available for push notifications feature.',
      })
    }

    const existingRequest = PushNotificationsFeature.tokenSyncInFlightByUser.get(resolvedUserId)
    if (existingRequest) {
      return existingRequest
    }

    const syncRequest = (async () => {
      try {
        return await addPushNotificationsTokenService(resolvedUserId)
      } catch (error) {
        if (error instanceof CoreError) {
          throw error
        }
        throw new CoreError({
          cause: CoreErrorCause.PushNotificationsRequestFailure,
          context: 'Failed to sync push notifications token for the authenticated user.',
          underlyingException: error,
        })
      } finally {
        PushNotificationsFeature.tokenSyncInFlightByUser.delete(resolvedUserId)
      }
    })()

    PushNotificationsFeature.tokenSyncInFlightByUser.set(resolvedUserId, syncRequest)

    return syncRequest
  }

  public static async removeTokenForAuthenticatedUser(userId?: string): Promise<void> {
    try {
      const resolvedUserId = userId ?? getAuthUserIdOrThrow()
      await removePushNotificationsTokenService(resolvedUserId)
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.PushNotificationsRequestFailure,
        context: 'Failed to remove push notifications token for the authenticated user.',
        underlyingException: error,
      })
    }
  }

  public static async enqueuePushForInAppNotification(
    params: EnqueuePushForInAppNotificationParams,
  ): Promise<string | null> {
    try {
      const senderId = params.notification.sender?.trim() || getAuthUserIdOrThrow()
      if (!senderId) {
        return null
      }

      const route = normalizeRoute(params.destination)

      return await triggerPushNotificationService({
        notificationTitle: toNotificationTitle(params.notification),
        notificationText: toNotificationText(params.notification),
        userRefs: [toUserRefPath(params.forUserId)],
        initialPageName: params.destination.pathname.trim() || '/',
        targetDeviceType: WEB_DEVICE_TYPE,
        parameterData: {
          link: route,
        },
        sender: senderId,
        timestamp: params.notification.createdTime ?? new Date(),
      })
    } catch (error) {
      if (error instanceof CoreError) {
        throw error
      }
      throw new CoreError({
        cause: CoreErrorCause.PushNotificationsRequestFailure,
        context: 'Failed to enqueue push notification for in-app notification.',
        underlyingException: error,
      })
    }
  }
}

export async function syncPushNotificationsToken(userId?: string): Promise<string | null> {
  return PushNotificationsFeature.syncTokenForAuthenticatedUser(userId)
}

export async function removePushNotificationsToken(userId?: string): Promise<void> {
  return PushNotificationsFeature.removeTokenForAuthenticatedUser(userId)
}

export async function enqueuePushForInAppNotification(
  params: EnqueuePushForInAppNotificationParams,
): Promise<string | null> {
  return PushNotificationsFeature.enqueuePushForInAppNotification(params)
}
